import { useCallback } from "react";
import ExcelJS from "exceljs";
import { format } from "date-fns";

function cellValue(value) {
    if (value === null || value === undefined) {
        return "";
    }
    if (value instanceof Date) {
        return value;
    }
    if (typeof value === "object") {
        return JSON.stringify(value);
    }
    return value;
}

function collectHeaders(rows) {
    const keys = new Set();
    for (const row of rows) {
        if (row && typeof row === "object") {
            Object.keys(row).forEach((key) => keys.add(key));
        }
    }
    return [...keys];
}

async function downloadWorkbook(workbook, fileName) {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
}

const useExporter = () => {
    const exportToExcel = useCallback(async (data, filename, sheetName = "Data") => {
        if (!Array.isArray(data) || data.length === 0) {
            console.warn("No data to export");
            return;
        }

        const headers = collectHeaders(data);
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(sheetName);

        worksheet.columns = headers.map((key) => ({
            header: key,
            key,
            width: 16,
        }));

        for (const row of data) {
            const values = {};
            for (const key of headers) {
                values[key] = cellValue(row?.[key]);
            }
            worksheet.addRow(values);
        }

        const fileName = `${filename}_${format(new Date(), "yyyy-MM-dd_HH-mm")}.xlsx`;
        await downloadWorkbook(workbook, fileName);
    }, []);

    const handleExport = useCallback(
        async (jsonData, filename) => {
            if (Array.isArray(jsonData)) {
                await exportToExcel(jsonData, filename);
                return;
            }

            if (typeof jsonData === "object" && jsonData !== null) {
                for (const key of Object.keys(jsonData)) {
                    if (Array.isArray(jsonData[key])) {
                        await exportToExcel(jsonData[key], `${filename}_${key}`, key);
                    }
                }
                return;
            }

            console.warn("Nothing to export, data is not an array or object");
        },
        [exportToExcel]
    );

    return { handleExport };
};

export default useExporter;
